import { DragIcon } from '@/components/icons';
import useDragIndicator, {
  DROP_OFFSET_ATTR,
  getDraggedDom,
} from '@/data/hook/shared/dnd/useDragPlaceholder';
import { ClassNameComponent } from '@/utils/ClassNameComponent';
import {
  DragDropContext,
  DragDropContextProps,
  Draggable,
  DraggableChildrenFn,
  DraggableProvided,
  DraggableStateSnapshot,
  DraggableStyle,
  Droppable,
  OnDragEndResponder,
  OnDragStartResponder,
  OnDragUpdateResponder,
} from '@hello-pangea/dnd';
import { CloudArrowUpIcon } from '@heroicons/react/24/outline';
import {
  Children,
  ComponentProps,
  ComponentType,
  PropsWithChildren,
  ReactNode,
  useContext,
} from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import ConditionalRenderer from '../ConditionalRenderer';
import { Tooltip } from '../dataDisplay/Tooltip';
import DragIndicator, { DragIndicatorProps } from './dragdrop/DragIndicator';
import DragIndicatorContext from './dragdrop/DragPlaceholderContext';

export { DragDropContext } from '@hello-pangea/dnd';
export type { DraggableProps } from '@hello-pangea/dnd';

type DroppableProps = {
  groupId: string;
  className?: string;
  renderClone?: DraggableChildrenFn;
  getContainerForClone?: () => HTMLElement;
  disabled?: boolean;
  indicator?: ComponentType<DragIndicatorProps>;
  placeholder?: ComponentType<DragDropPlaceholderProps>;
  type?: string;
};

type DragDropListProps = DragDropContextProps &
  DroppableProps & {
    customPlaceholder?: boolean;
  };

export const DragDropList = ({
  children,
  groupId,
  className,
  renderClone,
  getContainerForClone,
  disabled,
  type,
  customPlaceholder,
  onDragStart: onDragStartProp,
  onDragUpdate: onDragUpdateProp,
  onDragEnd: onDragEndProp,
  ...props
}: PropsWithChildren<DragDropListProps>) => {
  const {
    indicator,
    onDragStart: indicatorDragStart,
    onDragUpdate: indicatorDragUpdate,
    onDragEnd: indicatorDragEnd,
  } = useDragIndicator(customPlaceholder);

  const handleDragStart: OnDragStartResponder = (start, provided) => {
    indicatorDragStart(start);
    onDragStartProp?.(start, provided);
  };

  const handleDragUpdate: OnDragUpdateResponder = (update, provided) => {
    indicatorDragUpdate(update);
    onDragUpdateProp?.(update, provided);
  };

  const handleDragEnd: OnDragEndResponder = (result, provided) => {
    indicatorDragEnd();
    onDragEndProp(result, provided);
  };

  return (
    <DragDropContext
      {...props}
      onDragStart={handleDragStart}
      onDragUpdate={handleDragUpdate}
      onDragEnd={handleDragEnd}
    >
      <DragIndicatorContext.Provider value={{ indicator, parentId: groupId }}>
        <DroppableList
          groupId={groupId}
          type={type}
          className={className}
          renderClone={renderClone}
          getContainerForClone={getContainerForClone}
          disabled={disabled}
        >
          {children}
        </DroppableList>
      </DragIndicatorContext.Provider>
    </DragDropContext>
  );
};

export const DroppableList = ({
  groupId,
  type,
  className,
  disabled,
  renderClone,
  getContainerForClone,
  children,
  indicator: Indicator = DragIndicator,
  placeholder: Placeholder = DragDropPlaceholder,
}: PropsWithChildren<DroppableProps>) => {
  const { indicator: indicatorComputed, parentId } =
    useContext(DragIndicatorContext);
  const indicatorStyle =
    indicatorComputed?.groupId === groupId ? indicatorComputed.style : null;

  const isEmpty = Children.count(children) === 0;

  const isParent = parentId === groupId;

  return (
    <Droppable
      droppableId={groupId}
      type={type}
      isDropDisabled={disabled}
      renderClone={renderClone}
      getContainerForClone={getContainerForClone}
    >
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.droppableProps}
          className={twMerge('flex flex-col relative', className)}
        >
          {children}
          {!isEmpty && provided.placeholder}
          {indicatorStyle && snapshot.isDraggingOver && !isEmpty && (
            <Indicator style={indicatorStyle} />
          )}

          {isEmpty && !isParent && (
            <Placeholder isDraggingOver={snapshot.isDraggingOver} />
          )}
        </div>
      )}
    </Droppable>
  );
};

export type DragDropItemProps = {
  id: string;
  index: number;
  role?: string;
  dragIconHandle?: ReactNode;
  disabled?: boolean;
  tooltip?: string;
  className?: Pick<ClassNameComponent, 'base' | 'button' | 'div'>;
  hideDragIconHandleOnDisabled?: boolean;
};

export const DragDropItem = ({
  children,
  index,
  disabled,
  id,
  ...props
}: PropsWithChildren<DragDropItemProps>) => {
  return (
    <Draggable
      draggableId={id}
      index={index}
      isDragDisabled={disabled}
      key={id}
    >
      {(provided, snapshot) => (
        <DraggableInner
          provided={provided}
          snapshot={snapshot}
          id={id}
          disabled={disabled}
          {...props}
        >
          {children}
        </DraggableInner>
      )}
    </Draggable>
  );
};

type DraggableInnerProps = {
  provided: DraggableProvided;
  snapshot: DraggableStateSnapshot;
  id?: string;
} & Omit<DragDropItemProps, 'index' | 'id'>;

const withDropTransform = (
  style: DraggableStyle | undefined,
  snapshot: DraggableStateSnapshot,
  id?: string,
): DraggableStyle | undefined => {
  if (!snapshot.isDropAnimating || !snapshot.dropAnimation) return style;
  const { x, y } = snapshot.dropAnimation.moveTo;
  if (x !== 0 || y !== 0) return style;
  const draggedEl = id ? getDraggedDom(id) : null;
  const dropOffset = Number(draggedEl?.getAttribute(DROP_OFFSET_ATTR) ?? 0);
  return { ...style, transform: `translate(0px, ${-dropOffset}px)` };
};

export const DraggableInner = ({
  provided,
  snapshot,
  id,
  role,
  className,
  dragIconHandle,
  hideDragIconHandleOnDisabled,
  disabled,
  tooltip,
  children,
}: PropsWithChildren<DraggableInnerProps>) => {
  return (
    <div
      ref={provided.innerRef}
      data-testid={id}
      role={role}
      {...provided.draggableProps}
      style={withDropTransform(provided.draggableProps.style, snapshot, id)}
      className={twMerge('gap-1.5 flex items-start', className?.base)}
    >
      <div
        {...provided.dragHandleProps}
        className={twMerge('relative cursor-pointer', className?.button)}
        data-testid={'dndHandle-' + id}
      >
        <ConditionalRenderer
          condition={!hideDragIconHandleOnDisabled || !disabled}
        >
          <Tooltip text={tooltip}>
            {dragIconHandle || <DragIcon className="w-4" />}
          </Tooltip>
        </ConditionalRenderer>
      </div>
      <div
        className={twMerge(
          !disabled ? 'w-[calc(100%-22px)]' : 'w-full',
          className?.div,
        )}
      >
        {children}
      </div>
    </div>
  );
};

type DragDropPlaceholderProps = {
  text?: string;
  isDraggingOver?: boolean;
} & ComponentProps<'div'>;

export const DragDropPlaceholder = ({
  isDraggingOver,
  text,
  className,
  ...props
}: DragDropPlaceholderProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'common',
  });
  const displayText = text || t('dndPlaceholder');

  const classes = twMerge(
    'relative w-full h-32 rounded-2xl flex flex-col items-center justify-center gap-2 bg-base-200 border border-dashed border-neutral-content',
    isDraggingOver && 'bg-primary-content border-primary/40',
    className,
  );
  return (
    <div className={classes} {...props}>
      <CloudArrowUpIcon className="w-6 h-6 text-base-content/50" />
      <p className="text-base-content/50 text-sm">{displayText}</p>
    </div>
  );
};
